<template>
  <div
    ref="bodyScrollEl"
    class="qtl-body-scroll"
    :class="bodyScrollClass"
  >
    <!--
      Результаты формул. Лежат внутри скролл-вьюпорта над таблицей и не sticky,
      поэтому уезжают вместе с шапкой при прокрутке. Значение форматируем тем же
      formatNumber, что и числа в ячейках.
    -->
    <div
      v-if="formulaResults && formulaResults.length"
      class="qtl-formulas"
    >
      <div
        v-for="(fr, i) in formulaResults"
        :key="i"
        class="qtl-formulas__item"
      >
        {{ fr.Name }}: {{ formatNumber(fr.Value) }}
      </div>
    </div>

    <!--
      Одна таблица: настройка колонок, прилипающая шапка и строки. Ширины колонок
      считает сам браузер по содержимому шапки и строк, как было раньше. Шапка и
      строки в одной таблице, поэтому колонки совпадают сами собой и крутятся вбок
      вместе.

      Жёсткую ширину колонке задаём только если пользователь сам её растянул (или
      она пришла сохранённой), либо на время перезагрузки, когда мы запоминаем
      текущие ширины. Остальное браузер раскидывает сам.
    -->
    <table
      ref="tableEl"
      class="qtl-table qtl-table--body"
      :style="tableStyle"
    >
      <colgroup>
        <col
          v-for="col in columns"
          :key="col.id"
          :data-col-id="col.id"
          :style="colStyle(col)"
        />
      </colgroup>

      <QTLiteHeader
        :columns="columns"
        :header-mode="headerMode"
        :header-checkbox="headerCheckbox"
        :WA="WA"
        :multiedit-allowed="multieditAllowed"
        :order-by-pinned="orderByPinned"
        :show-global-pinned-sort="showGlobalPinnedSort"
        :blocked="!!scrollLocked"
        @headerCheckboxChange="$emit('headerCheckboxChange', $event)"
        @sort="$emit('sort', $event)"
        @multieditClick="$emit('multieditClick', $event)"
        @togglePinnedOrder="$emit('togglePinnedOrder')"
      />

      <!--
        Тело таблицы. Пока ширины не зафиксированы (фаза замера по сэмплу) или
        виртуализация выключена (режим page / маленькая таблица) — рисуем плоский
        tbody со всеми (или сэмплом) строк. Когда ширины зафиксированы и виртуализация
        включена — держим в DOM только видимое окно строк плюс распорки сверху/снизу.
      -->
      <tbody v-if="!useVirtual" class="qtl-tbody">
        <QTLiteRow
          v-for="(row, idx) in renderedRows"
          :key="`${row.entityId ?? 'row'}:${idx}`"
          :row="row"
          :row-index="idx"
          :row-number-base="rowNumberBase"
          :columns="columns"
          :width-stamp="rowHeightAlignStamp"
          :src-element="srcElement"
          :WA="WA"
          :is-checked="isRowChecked(row.entityId)"
          @toggleCheckbox="$emit('toggleCheckbox', $event)"
          @changeHoverRecord="$emit('changeHoverRecord', $event)"
          @changeHeights="$emit('changeHeights', $event)"
          @refreshNeeded="$emit('refreshNeeded')"
          @updatePerson="$emit('updatePerson', $event)"
          @updateSelection="$emit('updateSelection', $event)"
          @checkBoxClick="$emit('checkBoxClick')"
          @sourceItemsRequested="$emit('sourceItemsRequested', $event)"
        />
      </tbody>

      <tbody v-else class="qtl-tbody">
        <tr v-if="padTop > 0" class="qtl-vpad" aria-hidden="true">
          <td :colspan="colCount" :style="{ height: padTop + 'px' }" />
        </tr>
        <QTLiteRow
          v-for="w in windowRows"
          :key="w.vr.key"
          :ref="registerRow"
          :data-index="w.vr.index"
          :row="w.row"
          :row-index="w.vr.index"
          :row-number-base="rowNumberBase"
          :columns="columns"
          :width-stamp="rowHeightAlignStamp"
          :src-element="srcElement"
          :WA="WA"
          :is-checked="isRowChecked(w.row.entityId)"
          @toggleCheckbox="$emit('toggleCheckbox', $event)"
          @changeHoverRecord="$emit('changeHoverRecord', $event)"
          @changeHeights="$emit('changeHeights', $event)"
          @refreshNeeded="$emit('refreshNeeded')"
          @updatePerson="$emit('updatePerson', $event)"
          @updateSelection="$emit('updateSelection', $event)"
          @checkBoxClick="$emit('checkBoxClick')"
          @sourceItemsRequested="$emit('sourceItemsRequested', $event)"
        />
        <tr v-if="padBottom > 0" class="qtl-vpad" aria-hidden="true">
          <td :colspan="colCount" :style="{ height: padBottom + 'px' }" />
        </tr>
      </tbody>
    </table>
    <div
      v-if="!rows.length && !loading"
      class="qtl-empty"
    >
      <slot name="empty">{{ emptyMessage }}</slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type CSSProperties } from "vue";
import type { VirtualItem } from "@tanstack/vue-virtual";
import QTLiteHeader from "./QTLiteHeader.vue";
import QTLiteRow from "./QTLiteRow.vue";
import { isColumnDragLayoutAuto } from "../composables/resizeState";
import type { LiteColumn, LiteRow } from "../types";
import type { TableFormulaResult } from "@/models/interfaces/entity-settings/querytable/querytable";
import { formatNumber } from "../cell-items-lite/cellFormat";

const props = withDefaults(defineProps<{
  /** если true, рисуем только видимое окно строк (виртуализация). по умолчанию выключено */
  virtualized?: boolean;
  /** окно видимых строк от виртуализатора (@tanstack/vue-virtual) */
  virtualRows?: VirtualItem[];
  /** суммарная высота всех строк в px, от виртуализатора */
  totalSize?: number;
  /** отступ начала списка (tbody) от верха контента скролла: формулы + шапка */
  scrollMargin?: number;
  /** регистрирует DOM-строку в виртуализаторе для замера её высоты */
  measureElement?: (el: HTMLElement | null) => void;
  rows: LiteRow[];
  /**
   * фаза замера ширин: если задано число, рисуем только первые N строк. По ним
   * браузер дёшево раскладывает колонки (авто-раскладка стоит пропорционально числу
   * строк), мы снимаем ширины и дальше держим всю таблицу в fixed-layout. null —
   * рисуем все строки (обычный режим, ширины уже зафиксированы).
   */
  measureSampleSize?: number | null;
  columns: LiteColumn[];
  /** как ведёт себя шапка и прокрутка: sticky, scroll или page */
  headerMode?: "sticky" | "scroll" | "page";
  /**
   * ширины колонок, которые пользователь сам задал или которые пришли
   * сохранёнными. только им ставим жёсткую ширину, остальное браузер раскидает сам
   */
  userWidths: Record<string, number>;
  /**
   * запомненные ширины всех колонок на время перезагрузки (смотри QueryTableLite).
   * пока заданы, ставим их всем колонкам, чтобы шапка не дёргалась. браузер всё
   * равно сможет расширить ту колонку, где новые данные не влезли
   */
  frozenColWidths?: Record<string, number> | null;
  /**
   * живые ширины двух колонок во время ручного таскания границы (только при
   * виртуализации). перекрывают userWidths/frozenColWidths, чтобы ре-рендер тела,
   * который провоцирует замер высот виртуализатором при переносе текста, не возвращал
   * <col> к исходной ширине
   */
  dragLiveWidths?: Record<string, number>;
  /**
   * у всех колонок есть запомненная ширина. тогда держим таблицу в режиме жёстких
   * ширин, чтобы браузер ничего не пересчитывал сам и ничего не скакало
   */
  widthsLocked?: boolean;
  /** сумма всех запомненных ширин в пикселях, ширина таблицы в жёстком режиме */
  lockedWidthPx?: number;
  /**
   * во сколько раз таблица шире окна (в диалоге и секторе 1, иначе берётся из
   * настройки ширины). задаём как минимальную ширину таблицы, а браузер уже сам
   * раскидывает это место между колонками
   */
  effectiveMultiplier: number;
  /** метка, по смене которой строки заново выравнивают высоты параметров */
  rowHeightAlignStamp?: string;
  srcElement: any;
  WA: boolean;
  isRowChecked: (entityId: number) => boolean;
  /** номер первой строки страницы (= pagination offset), для сквозной нумерации */
  rowNumberBase?: number;
  /** результаты формул; рисуем их над таблицей внутри скролла, чтобы уезжали с шапкой */
  formulaResults?: TableFormulaResult[];
  /** во время обновления (видна полоса загрузки) прокрутку запрещаем, позицию держим */
  scrollLocked?: boolean;
  loading: boolean;
  emptyMessage: string;
  /** как показывать строки во время обновления: обычно, серым или совсем спрятать */
  contentMode?: "normal" | "dim" | "hide";
  /* --- то, что просто пробрасываем в шапку --- */
  headerCheckbox: string;
  multieditAllowed?: boolean;
  orderByPinned?: boolean;
  showGlobalPinnedSort?: boolean;
}>(), {
  virtualized: false,
  virtualRows: () => [],
  totalSize: 0,
  scrollMargin: 0,
  measureElement: undefined,
  contentMode: "normal",
  frozenColWidths: null,
  headerMode: "sticky",
  measureSampleSize: null,
});

/* Виртуализируем только когда ширины уже зафиксированы (fixed-layout). Пока идёт
   фаза замера ширин по сэмплу, рисуем плоский tbody с сэмплом — виртуализатору
   нужны стабильные ширины, иначе окно строк снова влияло бы на раскладку. */
const useVirtual = computed<boolean>(() => !!props.virtualized && !!props.widthsLocked);

/* В плоском пути (фаза замера или виртуализация выключена) рисуем сэмпл или все строки.
   Это единственное место, где решается, сколько строк реально попадёт в DOM. */
const renderedRows = computed<LiteRow[]>(() => {
  const n = props.measureSampleSize;
  if (n != null && n >= 0 && n < props.rows.length) return props.rows.slice(0, n);
  return props.rows;
});

const colCount = computed<number>(() => props.columns.length);

/* Высота верхней/нижней распорки: переводим абсолютные (с учётом scrollMargin)
   офсеты виртуальных строк в координаты внутри tbody. Сумма распорок и высот
   отрисованных строк = totalSize. */
const padTop = computed<number>(() => {
  const first = props.virtualRows[0];
  return first ? Math.max(0, first.start - props.scrollMargin) : 0;
});
const padBottom = computed<number>(() => {
  const arr = props.virtualRows;
  const last = arr[arr.length - 1];
  if (!last) return 0;
  return Math.max(0, props.totalSize - (last.end - props.scrollMargin));
});

/* Пара «виртуальная строка + её данные». После резкой смены данных виртуализатор
   может кратко вернуть индекс за пределами rows — такие пары отсекаем, чтобы шаблон
   не читал rows[index] как undefined. */
const windowRows = computed(() => props.virtualRows
  .map((vr) => ({ vr, row: props.rows[vr.index] }))
  .filter((x): x is { vr: VirtualItem; row: LiteRow } => !!x.row));

/* Регистрируем корневой <tr> строки в виртуализаторе для замера высоты. Функциональный
   ref на компонент отдаёт его инстанс — берём у него экспонированный rootEl (или $el). */
function registerRow(comp: any) {
  const el: HTMLElement | null = comp ? (comp.rootEl ?? comp.$el ?? null) : null;
  props.measureElement?.(el);
}

defineEmits<{
  (e: "toggleCheckbox", payload: { entityId: number; checked: boolean }): void;
  (e: "changeHoverRecord", payload: any): void;
  (e: "changeHeights", payload: any): void;
  (e: "refreshNeeded"): void;
  (e: "updatePerson", payload: { ev: any; entityId: number }): void;
  (e: "updateSelection", payload: { entityId: number; checked: boolean }): void;
  (e: "checkBoxClick"): void;
  (e: "sourceItemsRequested", payload: { ids: number[]; type: number; entityId: number }): void;
  (e: "headerCheckboxChange", value: string): void;
  (e: "sort", payload: { col: LiteColumn; nextOrder: number }): void;
  (e: "multieditClick", ev: MouseEvent): void;
  (e: "togglePinnedOrder"): void;
}>();

const bodyScrollEl = ref<HTMLElement | null>(null);
const tableEl = ref<HTMLTableElement | null>(null);

defineExpose({
  get bodyScrollEl() { return bodyScrollEl.value; },
  get tableEl() { return tableEl.value; },
});

const tableStyle = computed<CSSProperties>(() => {
  if (props.widthsLocked && (props.lockedWidthPx ?? 0) > 0) {
    // идёт таскание границы: держим auto-раскладку, чтобы браузер не давал ужать колонку
    // уже содержимого (текст переносится, а не обрезается), но ширину таблицы фиксируем в
    // px — место перераспределяется только между двумя колонками. Ширины колонок задаёт
    // dragLiveWidths (см. colStyle). Без этого ре-рендер виртуализатора вернул бы fixed.
    if (isColumnDragLayoutAuto.value) {
      return { tableLayout: "auto", width: `${props.lockedWidthPx}px` };
    }
    // все ширины известны: держим их жёстко, браузер ничего не пересчитывает сам
    return { tableLayout: "fixed", width: `${props.lockedWidthPx}px` };
  }
  // ширины ещё не зафиксированы: пусть браузер сам разложит колонки по содержимому,
  // но не уже заданной доли окна
  return {
    tableLayout: "auto",
    width: "min-content",
    minWidth: `${props.effectiveMultiplier * 100}%`,
  };
});

function colStyle(col: LiteColumn): CSSProperties | undefined {
  // во время таскания границы при виртуализации живая ширина имеет приоритет: иначе
  // ре-рендер тела вернул бы колонку к исходной ширине (см. dragLiveWidths)
  const live = props.dragLiveWidths;
  if (live && live[col.id] > 0) return { width: `${live[col.id]}px` };
  const frozen = props.frozenColWidths;
  if (frozen && frozen[col.id] > 0) return { width: `${frozen[col.id]}px` };
  if (col.isMultiedit) return { width: "55px" };
  const w = props.userWidths[col.id];
  return w > 0 ? { width: `${w}px` } : undefined;
}

const bodyScrollClass = computed(() => ({
  "qtl-body-scroll--dim": props.contentMode === "dim",
  "qtl-body-scroll--hide": props.contentMode === "hide",
  "qtl-body-scroll--page": props.headerMode === "page",
  "qtl-body-scroll--locked": !!props.scrollLocked,
}));
</script>

<style lang="scss" scoped>
.qtl-body-scroll {
  flex: 1 1 auto;
  /* Фон прозрачный: белой «карточкой» теперь является сама таблица (рамка со
     скруглением на .qtl-table--body), а вокруг неё и под блоком формул просвечивает
     фон страницы (#F2F2F2). */
  background: transparent;
  /* единственное место, где есть прокрутка, и вбок, и вниз */
  overflow-x: auto;
  overflow-y: auto;
  /* убираем оттягивание на краях по обеим осям, чтобы не было резинового
     подлагивания при упирании в края прокрутки */
  overscroll-behavior: none;
  scroll-behavior: auto;
  position: relative;
  /* contain без size, иначе блок схлопывается, когда у родителя высота по содержимому */
  contain: layout style paint;
  min-height: 0;
  /* Тонкие скроллы только у таблицы (глобальные в quasar.scss не трогаем). Firefox. */
  scrollbar-width: thin;
  scrollbar-color: #d0d0d0 transparent;
}

/* WebKit/Blink: те же 6px. Плоские селекторы, без вложенности. */
.qtl-body-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.qtl-body-scroll::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 6px;
}

.qtl-body-scroll::-webkit-scrollbar-track {
  background: transparent;
}

/* Пока идёт обновление и видна полоса загрузки, прокрутку запрещаем. overflow:hidden
   сохраняет текущий scrollTop/scrollLeft, поэтому позиция таблицы не дёргается. */
.qtl-body-scroll--locked {
  overflow: hidden;
}

/* Блок результатов формул над таблицей. Цвет как у легаси (text-deep-purple-8).
   Не sticky: уезжает вместе с содержимым при прокрутке. */
.qtl-formulas {
  padding: 4px 8px;
}

.qtl-formulas__item {
  color: #4527a0;
  font-size: 13px;
  line-height: 1.4;
}

/*
  Режим page: своей прокрутки у таблицы нет, она растёт по содержимому и
  прокручивается вместе со страницей. Поэтому ничего не обрезаем и не прокручиваем.
*/
.qtl-body-scroll--page {
  overflow: visible;
  contain: none;
}

.qtl-table--body {
  /*
    Рамка-карточка таблицы начинается ровно от шапки. Бордер со скруглением живёт
    на самой таблице, поэтому блок формул, лежащий над таблицей внутри того же
    скролла, остаётся ВНЕ рамки, но при этом продолжает уезжать вместе с шапкой.

    border-collapse держим separate со spacing 0: при collapse браузер игнорирует
    border-radius у таблицы и углы не скругляются. Удвоения внутренних линий нет —
    у ячеек только border-right/border-bottom, а у соседей слева/сверху границ нет.

    overflow для обрезки углов СОЗНАТЕЛЬНО не используем: любой overflow != visible
    между thead и скролл-контейнером сломал бы sticky-режим шапки (она прилипала бы
    к таблице, а не к вьюпорту прокрутки). Поэтому углы скругляем не клипом, а
    скруглением крайних ячеек шапки и последней строки ниже.
  */
  border-collapse: separate;
  border-spacing: 0;
  /* внешняя рамка таблицы под цвет фона #F2F2F2 (внутренние линии сетки остаются #D2D2D2) */
  border: 1px solid #F2F2F2;
  border-radius: 4px;
  background: #fff;
  margin: 0;
}

/* Верхние углы карточки = крайние ячейки шапки. */
.qtl-table--body :deep(thead th:first-child) {
  border-top-left-radius: 4px;
}

.qtl-table--body :deep(thead th:last-child) {
  border-top-right-radius: 4px;
}

/* Нижние углы карточки = крайние ячейки последней строки. У них же снимаем нижнюю
   границу ячейки, чтобы низ рисовала только рамка таблицы (без 2px-линии). */
.qtl-table--body :deep(tbody tr:last-child td) {
  border-bottom: none;
}

.qtl-table--body :deep(tbody tr:last-child td:first-child) {
  border-bottom-left-radius: 4px;
}

.qtl-table--body :deep(tbody tr:last-child td:last-child) {
  border-bottom-right-radius: 4px;
}

.qtl-row {
  background: #fff;
  color: #333333;
  font-size: 12px;
}

.qtl-row--even {
  background: #f9f9f9;
}

.qtl-row:hover,
.qtl-row--even:hover {
  background: #f2f2f2;
}

.qtl-row--checked {
  background-color: #f3f8f4;
}

/* Подсветку строки под полоской ресайза вешает слой ресайза классом qtl-row--hover
   (штатный :hover туда не доходит — полоска перекрывает строку). Даём тот же фон, что
   и :hover, но с запасом по специфичности через .qtl-tbody, чтобы перебить базовый,
   чётный и выделенный фон в любом порядке подключения стилей. */
.qtl-tbody .qtl-row--hover {
  background: #f2f2f2;
}

/* Распорки виртуализации: держат суммарную высоту прокрутки за пределами окна строк.
   Никаких границ, паддингов и фона — только заданная высота. */
.qtl-vpad td {
  padding: 0;
  border: 0;
  background: transparent;
}

/* Во время обновления строки чуть бледнеют и на них не нажать (так по умолчанию). */
.qtl-body-scroll--dim .qtl-tbody {
  opacity: 0.5;
  filter: grayscale(0.35);
  pointer-events: none;
  transition: opacity 0.12s ease;
}

/* Во время обновления строки совсем прячем, но место под них остаётся. */
.qtl-body-scroll--hide .qtl-tbody {
  visibility: hidden;
}

.qtl-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 14px;
  pointer-events: none;
}
</style>
